import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
    Handshake,
    MapPin,
    Calendar,
    CreditCard,
    Loader2,
    AlertCircle,
    CheckCircle2,
    Clock,
    TrendingUp,
    FileText,
    Search,
    AlertTriangle,
    ChevronRight,
    ArrowLeft,
    FileSpreadsheet,
    Printer
} from 'lucide-react';
import config from '../config';

const InstallmentDetails = ({ agreementId }) => {
    const [installments, setInstallments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const fetchInstallments = async () => {
            const token = sessionStorage.getItem('token');
            try {
                const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-convenios/${agreementId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (!response.ok) throw new Error();
                const data = await response.json();
                setInstallments(data);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchInstallments();
    }, [agreementId]);

    if (loading) return (
        <div className="flex items-center gap-2 p-6 text-sky-600 font-bold text-sm">
            <Loader2 className="animate-spin" size={16} /> Cargando detalle de cuotas...
        </div>
    );

    if (error) return (
        <div className="p-6 text-red-500 text-xs font-bold flex items-center gap-2">
            <AlertCircle size={16} /> No se pudieron cargar los detalles de las cuotas.
        </div>
    );

    return (
        <div className="w-full">
            <div className="md:hidden space-y-4 p-4">
                {installments.map((cuota, idx) => (
                    <div key={idx} className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
                        <div className="flex justify-between items-start mb-3">
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Cuota</p>
                                <p className="text-sm font-bold text-slate-700">#{cuota.num_cuota}</p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-black uppercase border ${cuota.is_pagada ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                                {cuota.is_pagada ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                                {cuota.is_pagada ? 'Pagada' : 'Pendiente'}
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Mes / Año</p>
                                <p className="text-xs font-bold text-slate-600 uppercase">{cuota.desc_mes} {cuota.ano_cuota}</p>
                            </div>
                            <div>
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest text-right">Monto</p>
                                <p className="text-sm font-black text-slate-800 text-right">
                                    {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(cuota.monto_cuota)}
                                </p>
                            </div>
                        </div>
                        {cuota.fecha_pago && (
                            <div className="mt-3 pt-3 border-t border-slate-50">
                                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Pagado el</p>
                                <p className="text-xs font-medium text-slate-600">{new Date(cuota.fecha_pago).toLocaleDateString('es-CL')}</p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                    <thead>
                    <tr className="bg-slate-50 border-y border-slate-100">
                        <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Cuota</th>
                        <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Mes / Año</th>
                        <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Monto</th>
                        <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Estado</th>
                        <th className="px-6 py-3 text-[10px] font-black uppercase tracking-widest text-slate-400">Fecha Pago</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                    {installments.map((cuota, idx) => (
                        <tr key={idx} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4 text-xs font-bold text-slate-700">#{cuota.num_cuota}</td>
                            <td className="px-6 py-4 text-xs font-medium text-slate-600 uppercase">
                                {cuota.desc_mes} {cuota.ano_cuota}
                            </td>
                            <td className="px-6 py-4 text-xs font-black text-slate-800">
                                {new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(cuota.monto_cuota)}
                            </td>
                            <td className="px-6 py-4">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-black uppercase border ${cuota.is_pagada ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-amber-50 text-amber-600 border-amber-100'}`}>
                                        {cuota.is_pagada ? <CheckCircle2 size={10} /> : <Clock size={10} />}
                                        {cuota.is_pagada ? 'Pagada' : 'Pendiente'}
                                    </span>
                            </td>
                            <td className="px-6 py-4 text-xs text-slate-500 font-medium">
                                {cuota.fecha_pago ? new Date(cuota.fecha_pago).toLocaleDateString('es-CL') : '---'}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default function Agreements() {
    const { selectedProperty } = useOutletContext();
    const navigate = useNavigate();

    const [view, setView] = useState('list');
    const [agreements, setAgreements] = useState([]);
    const [selectedAgreement, setSelectedAgreement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [processingPdfId, setProcessingPdfId] = useState(null);

    const fetchAgreements = async () => {
        if (!selectedProperty?.nro_cliente) return;
        const token = sessionStorage.getItem('token');
        if (!token) { navigate('/login'); return; }

        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-convenios?nro_cliente=${selectedProperty.nro_cliente}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (response.status === 401) { sessionStorage.clear(); navigate('/login'); return; }
            if (!response.ok) throw new Error();
            const data = await response.json();
            setAgreements(Array.isArray(data) ? data : []);
        } catch (err) {
            setError("No se pudieron cargar tus convenios.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewPDF = async (id) => {
        const token = sessionStorage.getItem('token');
        if (!token) return;
        setProcessingPdfId(id);
        try {
            const response = await fetch(`${config.apiBaseUrl}/api/sucursal-virtual/client-convenios-pdf?convenio_id=${id}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) throw new Error();
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
        } catch (err) {
            console.error(err);
        } finally {
            setProcessingPdfId(null);
        }
    };

    const handleExportCSV = () => {
        if (agreements.length === 0) return;

        const headers = ["Folio", "Monto Total", "Cuotas Pagadas", "Cuotas Totales", "Fecha Creación", "Estado"];
        const rows = agreements.map(conv => [
            conv.folio,
            conv.monto_total,
            conv.nro_cuotas_pagadas,
            conv.numero_cuotas,
            formatDate(conv.fecha_creacion),
            conv.convenio_activo ? 'ACTIVO' : 'FINALIZADO'
        ]);

        const csvContent = [
            headers.join(";"),
            ...rows.map(r => r.join(";"))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `convenios-cliente-${selectedProperty?.nro_cliente}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePrint = () => {
        window.print();
    };

    useEffect(() => {
        fetchAgreements();
        setView('list');
    }, [selectedProperty]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const handleSelectAgreement = (conv) => {
        setSelectedAgreement(conv);
        setView('detail');
        window.scrollTo(0, 0);
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center py-24 text-sky-600">
            <Loader2 className="animate-spin mb-4" size={48} />
            <p className="font-bold text-lg">Buscando acuerdos...</p>
        </div>
    );

    if (error) return (
        <div className="bg-red-50 border border-red-100 p-8 rounded-3xl flex flex-col items-center text-center gap-4 text-red-700 max-w-2xl mx-auto">
            <AlertCircle size={40} className="opacity-50" />
            <div><p className="font-bold text-lg">Error de carga</p><p className="text-sm opacity-80">{error}</p></div>
            <button onClick={fetchAgreements} className="px-6 py-2 bg-red-100 hover:bg-red-200 rounded-xl font-bold transition-all shadow-sm">Reintentar</button>
        </div>
    );

    return (
        <div className="max-w-6xl mx-auto w-full animate-in fade-in duration-500 pb-20">
            {/* Estilos para "Imprimir / PDF": oculta los controles y evita cortar convenios entre páginas */}
            <style>
                {`
                @media print {
                    @page { size: auto; margin: 15mm; }
                    body, html, #root, main, .overflow-y-auto {
                        background: white !important;
                        overflow: visible !important;
                        height: auto !important;
                        position: static !important;
                    }
                    .no-print { display: none !important; }
                    .print-full { overflow: visible !important; height: auto !important; }
                    .agreement-card, .detail-card { break-inside: avoid; box-shadow: none !important; }
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
                `}
            </style>

            {view === 'list' ? (
                <>
                    <div className="mb-8 lg:mb-10 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <h1 className="text-3xl lg:text-4xl font-black text-slate-800 mb-2">Mis Convenios</h1>
                            <p className="text-slate-500 font-medium">Listado de acuerdos de pago registrados.</p>
                        </div>

                        {agreements.length > 0 && (
                            <div className="flex items-center gap-3 no-print">
                                <button
                                    onClick={handleExportCSV}
                                    className="px-4 py-2.5 bg-white text-emerald-600 rounded-xl font-bold border border-slate-100 hover:border-sky-200 transition-all flex items-center justify-center gap-2 text-sm shadow-sm active:scale-95"
                                >
                                    <FileSpreadsheet size={18} />
                                    Descargar CSV
                                </button>
                                <button
                                    onClick={handlePrint}
                                    className="px-4 py-2.5 bg-sky-600 text-white rounded-xl font-bold hover:bg-sky-700 transition-all flex items-center justify-center gap-2 text-sm shadow-md shadow-sky-100 active:scale-95"
                                >
                                    <Printer size={18} />
                                    Imprimir / PDF
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="mb-8 bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center gap-4">
                        <div className="p-3 bg-sky-100 text-sky-600 rounded-2xl w-fit"><MapPin size={24} /></div>
                        <div className="flex-1">
                            <label className="block text-slate-400 text-[10px] font-black uppercase tracking-widest mb-0.5 ml-1">Servicio Activo</label>
                            <div className="text-slate-700 font-bold text-lg leading-tight">#{selectedProperty?.nro_cliente} - {selectedProperty?.up_direccion}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {agreements.length === 0 ? (
                            <div className="text-center py-24 bg-white rounded-3xl border border-dashed border-slate-200">
                                <Handshake className="mx-auto text-slate-200 mb-4" size={64} />
                                <p className="text-slate-400 font-bold">No registras convenios históricos para este servicio.</p>
                            </div>
                        ) : (
                            agreements.map((conv) => {
                                const progress = (conv.nro_cuotas_pagadas / conv.numero_cuotas) * 100;
                                return (
                                    <div
                                        key={conv.id}
                                        onClick={() => handleSelectAgreement(conv)}
                                        className="agreement-card group bg-white rounded-3xl p-5 md:p-6 border border-slate-100 shadow-sm hover:shadow-md hover:border-sky-200 transition-all cursor-pointer flex flex-col md:flex-row items-center gap-6"
                                    >
                                        <div className="p-4 bg-sky-50 text-sky-600 rounded-2xl group-hover:bg-sky-600 group-hover:text-white transition-colors shrink-0">
                                            <Handshake size={24} />
                                        </div>
                                        <div className="flex-1 w-full">
                                            <div className="flex flex-wrap items-center gap-2 mb-1">
                                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Folio #{conv.folio}</span>
                                                <span className={`px-2 py-0.5 rounded-full text-[8px] font-black uppercase border ${conv.convenio_activo ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-400 border-slate-100'}`}>
                                                    {conv.convenio_activo ? 'Activo' : 'Finalizado'}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-black text-slate-800 mb-2">{formatCurrency(conv.monto_total)}</h3>
                                            <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                                                <div className="h-full bg-sky-500 rounded-full transition-all duration-700" style={{ width: `${progress}%` }} />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 md:grid-cols-1 gap-x-8 gap-y-1 shrink-0 text-center md:text-right w-full md:w-auto border-t md:border-t-0 pt-4 md:pt-0">
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cuotas</p>
                                                <p className="text-xs font-bold text-slate-600">{conv.nro_cuotas_pagadas}/{conv.numero_cuotas}</p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Fecha</p>
                                                <p className="text-xs font-bold text-slate-600">{formatDate(conv.fecha_creacion)}</p>
                                            </div>
                                        </div>
                                        <div className="hidden md:block text-slate-300 group-hover:text-sky-500 group-hover:translate-x-1 transition-all no-print">
                                            <ChevronRight size={24} />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </>
            ) : (
                <div className="animate-in slide-in-from-right-4 duration-500 print-full">
                    <div className="flex items-center justify-between mb-6 no-print">
                        <button
                            onClick={() => setView('list')}
                            className="flex items-center gap-2 text-sky-600 font-bold text-sm hover:gap-3 transition-all"
                        >
                            <ArrowLeft size={18} /> Volver al listado
                        </button>
                        <button
                            onClick={handlePrint}
                            className="px-4 py-2.5 bg-white text-sky-600 rounded-xl font-bold border border-slate-100 hover:border-sky-200 transition-all flex items-center justify-center gap-2 text-sm shadow-sm active:scale-95"
                        >
                            <Printer size={18} />
                            Imprimir / PDF
                        </button>
                    </div>

                    <div className="detail-card bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden flex flex-col">
                        <div className="p-6 md:p-10 border-b border-slate-50">
                            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-10">
                                <div className="flex flex-col md:flex-row items-start md:items-center gap-5 w-full">
                                    <div className="p-5 bg-sky-600 text-white rounded-3xl shadow-lg shadow-sky-200 hidden md:block">
                                        <Handshake size={32} />
                                    </div>
                                    <div className="flex-1 w-full">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Detalle del Convenio</span>
                                            <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black uppercase border ${selectedAgreement.convenio_activo ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-slate-50 text-slate-600 border-slate-100'}`}>
                                                {selectedAgreement.convenio_activo ? 'Vigente' : 'Finalizado'}
                                            </span>
                                        </div>
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <h3 className="text-2xl font-black text-slate-800 tracking-tight">Folio Interno: #{selectedAgreement.folio}</h3>
                                            <button
                                                onClick={() => handleViewPDF(selectedAgreement.id)}
                                                disabled={processingPdfId !== null}
                                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-sky-600 text-white rounded-2xl text-sm font-black hover:bg-sky-700 shadow-lg shadow-sky-100 transition-all active:scale-95 disabled:opacity-70 no-print"
                                            >
                                                {processingPdfId === selectedAgreement.id ? <Loader2 className="animate-spin" size={18} /> : <FileText size={18} />}
                                                Ver Documento PDF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-slate-50 px-6 py-4 rounded-3xl border border-slate-100 w-full lg:w-auto text-center lg:text-right shrink-0">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-sky-600 mb-1">Monto Comprometido</p>
                                    <p className="text-3xl font-black text-slate-800 tracking-tighter">{formatCurrency(selectedAgreement.monto_total)}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
                                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-xl"><Calendar size={20} /></div>
                                    <div><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Creación</p><p className="text-sm font-bold text-slate-700">{formatDate(selectedAgreement.fecha_creacion)}</p></div>
                                </div>
                                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-xl"><Clock size={20} /></div>
                                    <div><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Inicio de Pago</p><p className="text-sm font-bold text-slate-700">{formatDate(selectedAgreement.fecha_primera_cuota)}</p></div>
                                </div>
                                <div className="bg-white p-5 rounded-2xl border border-slate-100 shadow-sm flex items-center gap-4">
                                    <div className="p-3 bg-slate-50 text-slate-400 rounded-xl"><TrendingUp size={20} /></div>
                                    <div><p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Término</p><p className="text-sm font-bold text-slate-700">{formatDate(selectedAgreement.fecha_ultima_cuota)}</p></div>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex justify-between items-end">
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Progreso de Cumplimiento</p>
                                        <p className="text-base font-black text-slate-800">{selectedAgreement.nro_cuotas_pagadas} de {selectedAgreement.numero_cuotas} cuotas cubiertas</p>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-2xl font-black text-sky-600 leading-none">{Math.round((selectedAgreement.nro_cuotas_pagadas / selectedAgreement.numero_cuotas) * 100)}%</p>
                                    </div>
                                </div>
                                <div className="h-4 w-full bg-slate-100 rounded-full p-1 shadow-inner">
                                    <div className="h-full bg-gradient-to-r from-sky-500 to-sky-600 rounded-full transition-all duration-1000 shadow-sm" style={{ width: `${(selectedAgreement.nro_cuotas_pagadas / selectedAgreement.numero_cuotas) * 100}%` }} />
                                </div>
                                <div className="grid grid-cols-2 gap-4 pt-2">
                                    <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                                        <CheckCircle2 size={16} className="text-emerald-500" />
                                        <span className="text-xs font-bold text-emerald-800">Pagado: {formatCurrency(selectedAgreement.montos_pagadas)}</span>
                                    </div>
                                    <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-xl border border-amber-100">
                                        <CreditCard size={16} className="text-amber-500" />
                                        <span className="text-xs font-bold text-amber-800">Pendiente: {formatCurrency(selectedAgreement.monto_total - selectedAgreement.montos_pagadas)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-slate-50/50">
                            <div className="px-6 md:px-10 py-4 flex items-center justify-between border-b border-slate-100">
                                <div className="flex items-center gap-2 text-slate-500">
                                    <Search size={16} />
                                    <h4 className="text-[10px] font-black uppercase tracking-widest">Detalle de Cuotas</h4>
                                </div>
                            </div>
                            <InstallmentDetails agreementId={selectedAgreement.id} />
                        </div>

                        {(selectedAgreement.articulo1 || selectedAgreement.articulo2) && (
                            <div className="bg-slate-800 p-6 md:p-8">
                                <div className="flex gap-4">
                                    <div className="p-2 bg-amber-400 text-slate-900 rounded-lg h-fit"><AlertTriangle size={18} /></div>
                                    <p className="text-[11px] leading-relaxed text-slate-300 font-medium italic">
                                        "{selectedAgreement.articulo1} {selectedAgreement.articulo2}"
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
